import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppState, SearchEngineType, SearchFilter } from 'src/app/shared/models/state';
import { CustomEventsService } from 'src/app/shared/services/custom-events.service';
import { GoogleMapService } from 'src/app/shared/services/google-map.service';
import { DoctorCentersHelperService } from 'src/app/shared/services/helpers/doctor-centers-helper.service';
import { StateService } from 'src/app/shared/services/state.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { ContextService } from '../../../services/ctx.service';

@Component({
  selector: 'app-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.scss']
})
export class FilterModalComponent implements OnInit, OnDestroy {
  radio: string;
  bookingType: { label: string, id: string; };
  bookingTypes: { label: string, id: string; }[];
  centerName: string;
  doctorName: string;
  state: Partial<AppState>;
  subs: Subscription;

  constructor(
    public utils: UtilsService,
    public ctx: ContextService,
    private dialog: MatDialog,
    private googleMapService: GoogleMapService,
    private doctorCentersHelper: DoctorCentersHelperService,
    private stateService: StateService
  ) {
    this.state = this.stateService.getStateValue();
    this.bookingTypes = this.getBookingTypes();
    this.setFilters();
    this.subs = new Subscription();
  }

  ngOnInit(): void {
    this.subs.add(
      this.stateService.getState().subscribe(state => {
        this.state = state;
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  close() {
    this.dialog.closeAll();
  }

  onCleanFilters() {
    this.close();
    const searchEngineType = this.state.searchEngineType;
    this.onCleanFiltersHandler(searchEngineType);
  }

  onSearch() {
    const searchEngineType = this.state.searchEngineType;
    this.onSearchHandler(searchEngineType);
    this.googleMapService.clearInfoWindowHandlers();
    this.googleMapService.clearMarkers();
    this.close();
  }

  showSpecialistSearchInput(): boolean {
    return (!this.state.searchParams?.subSpeciality || this.state.searchParams?.subSpeciality?.descripcionSubespecialidad === 'Todas')
      && !this.state.hideSpecialistsTab;
  }

  private onCleanFiltersHandler(searchEngineType: SearchEngineType) {
    const radio = this.googleMapService?.defaultRadio.toString();
    this.state.radio = radio;
    if (searchEngineType === 'medical-center') {
      this.doctorCentersHelper.getMedicalCentersAndDoctors({ ...this.state, radio, searchFilters: {} }, true);
      return;
    }

    if (searchEngineType === 'diagnostic-test') {
      this.doctorCentersHelper.getMedicalCentersAndDoctors({
        ...this.state,
        radio,
        tabIndex: 0,
        centersPage: 0,
        doctorsPage: 0,
        searchFilters: {},
      }, false, true);
    }

    if (searchEngineType === 'emergency-center') {
      this.doctorCentersHelper.getMedicalCentersAndDoctors({
        ...this.state,
        radio,
        tabIndex: 0,
        centersPage: 0,
        doctorsPage: 0,
        searchFilters: {
          urgency: true,
        },
      }, false, true);
    }

    if (searchEngineType === 'additional-services') {
      this.doctorCentersHelper.getMoreAdditionalServices({
        ...this.state,
        radio,
        tabIndex: 0,
        centersPage: 0,
        doctorsPage: 0,
        searchFilters: {},
      });
    }
  }

  private onSearchHandler(searchEngineType: SearchEngineType) {

    const searchFilters: Partial<SearchFilter> = {
      centerName: this.centerName,
      doctorName: this.doctorName
    };

    if (this.bookingType) {
      searchFilters[this.bookingType.id] = true;
    }

    if (searchEngineType === 'medical-center') {
      this.doctorCentersHelper.getMedicalCentersAndDoctors({
        ...this.state,
        centersPage: 0,
        doctorsPage: 0,
        radio: this.radio || null,
        searchFilters
      }, true);
      return;
    }

    if (searchEngineType === 'diagnostic-test') {
      this.doctorCentersHelper.getMedicalCentersAndDoctors({
        ...this.state,
        tabIndex: 0,
        centersPage: 0,
        doctorsPage: 0,
        searchFilters,
        radio: this.radio || null,
      }, false, true);
    }

    if (searchEngineType === 'emergency-center') {
      this.doctorCentersHelper.getMedicalCentersAndDoctors({
        ...this.state,
        tabIndex: 0,
        centersPage: 0,
        doctorsPage: 0,
        searchFilters: {...searchFilters, urgency: true},
        radio: this.radio || null,
      }, false, true);
    }

    if (searchEngineType === 'additional-services') {
      this.doctorCentersHelper.getAdditionalServices({
        ...this.state,
        tabIndex: 0,
        centersPage: 0,
        doctorsPage: 0,
        searchFilters,
        radio: this.radio || null,
      }, false);
    }

  }

  private getBookingTypes() {
    const bookingTypes = [];
    const searchResponse = this.state.medicalCentersResponse;
    if (searchResponse?.indMapfreSalud) {
      bookingTypes.push({
        label: 'Centros Médicos MAPFRE',
        id: 'mapfreSalud'
      });
    }
    if (this.utils.showOnlineMeeting(searchResponse, this.state)) {
      bookingTypes.push({
        label: 'Cita online',
        id: 'onlineBooking'
      });
    }

    if (this.utils.showEmergencyLabel(searchResponse, this.state)) {
      bookingTypes.push({
        label: 'Urgencias',
        id: 'urgency'
      });
    }

    return bookingTypes;
  }

  private setFilters() {
    const searchFilters = this.state?.searchFilters || {};
    this.radio = this.state?.radio;
    this.centerName = searchFilters?.centerName;
    this.bookingTypes.forEach(type => {
      if (searchFilters[type.id]) {
        this.bookingType = type;
      }
    });
  }
}
